import { BitkitBadge } from '@bitrise/bitkit-v2';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { CI_CONFIG_EXPERT_LAST_VIEWED_TURN_KEY } from '@/components/CIConfigExpert/useCIConfigExpertConversation';
import { useMutateUserMetadata, useUserMetadata } from '@/hooks/api/useUserMetadata';
import useGatedBitkitTour from '@/hooks/useGatedBitkitTour';
import { queryClient } from '@/utils/query';

export const CI_CONFIG_EXPERT_TOUR_KEY = 'ci_config_expert_tour';

/** Absent means the user hasn't been on this page since the highlight existed. */
type HighlightState = 'visited' | 'seen';

type Params = {
  enabled: boolean;
  hasConversationHistory: boolean;
  isLoading: boolean;
  targetRef: RefObject<HTMLElement | null>;
};

/**
 * The one-time feature highlight pointing at the Ask AI button. It opens on its own, one visit after
 * the first one — the first visit is the one another tour may be running on — and only for someone
 * who hasn't used the assistant yet. The ✕, "Got it" and the Ask AI button itself all record it as
 * seen, so it never returns, on any browser.
 */
const useAskAIFeatureHighlight = ({ enabled, hasConversationHistory, isLoading, targetRef }: Params) => {
  const {
    data: state,
    isError: isStateError,
    isLoading: isStateLoading,
  } = useUserMetadata<HighlightState>(CI_CONFIG_EXPERT_TOUR_KEY, {
    enabled,
  });
  // Same query key as the conversation's own read, so this is the cache rather than a second request.
  const {
    data: lastViewedTurn,
    isError: isTurnError,
    isLoading: isTurnLoading,
  } = useUserMetadata<string>(CI_CONFIG_EXPERT_LAST_VIEWED_TURN_KEY, { enabled });
  const { mutate: mutateUserMetadata } = useMutateUserMetadata({ survivesNavigation: true });

  const [written, setWritten] = useState<HighlightState | null>(null);
  // Both writes happen without an interaction, so they need a guard that survives a re-render inside
  // the same tick — the state alone would let a second call through.
  const writtenRef = useRef<HighlightState | null>(null);

  const record = useCallback(
    (next: HighlightState) => {
      if (writtenRef.current === next) {
        return;
      }

      writtenRef.current = next;
      setWritten(next);
      if (next === 'seen') {
        queryClient.setQueryData(['usermetadata', CI_CONFIG_EXPERT_TOUR_KEY], { data: { value: next } });
      }
      mutateUserMetadata({ [CI_CONFIG_EXPERT_TOUR_KEY]: next });
    },
    [mutateUserMetadata],
  );

  const isResolving = isLoading || isStateLoading || isTurnLoading;
  const hasUsedAssistant = hasConversationHistory || lastViewedTurn != null;

  useEffect(() => {
    if (!enabled || isResolving || isStateError || isTurnError || state != null || writtenRef.current != null) {
      return;
    }

    record(hasUsedAssistant ? 'seen' : 'visited');
  }, [enabled, hasUsedAssistant, isResolving, isStateError, isTurnError, record, state]);

  const tour = useGatedBitkitTour({
    scope: 'global',
    onStatusChange: ({ status }) => {
      if (status === 'dismissed') {
        record('seen');
      }
    },
    // The stored state decides, not what we have just written: recording this visit must not open the
    // tour on it, and recording `seen` closes it.
    open: enabled && !isResolving && written == null && state === 'visited',
    steps: [
      {
        actions: [{ action: 'dismiss', label: 'Got it' }],
        badge: (
          <BitkitBadge variant="subtle" colorVariant="orange">
            Beta
          </BitkitBadge>
        ),
        description:
          'Ask questions about your Workflows and Pipelines, or describe a change and get working YAML back. Free during the open beta.',
        id: 'ask-ai',
        target: () => targetRef.current,
        title: 'Meet the AI Assistant',
      },
    ],
  });

  return { tour };
};

export default useAskAIFeatureHighlight;
